import type { Storage } from "@volumental/util";
import Cookies from "js-cookie";
import psl from "psl";

// Branded type for journey IDs to prevent string mix-ups
export type JourneyId = string & { readonly __brand: unique symbol };

interface Config {
  storage?: Storage; // Optional: only for backward-compat migration from localStorage
  storageKey?: string; // Key for legacy storage lookup, defaults to '__vol-jid'
  overrideId?: JourneyId | string;
  cookieName?: string; // Cookie name, defaults to '__vol-jid'
  cookieExpires?: number; // Cookie expiration in days (e.g., 365 for 1 year)
}

export interface JourneyManager {
  GetId: () => JourneyId;
  Exists: () => boolean;
}

function generateId(): JourneyId {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID() as JourneyId;
  }

  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.trunc(Math.random() * 16);
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  }) as JourneyId;
}

/**
 * Extracts the TLD domain for setting wildcard cookies.
 * Uses the Public Suffix List to correctly handle domains like .co.uk
 * @param hostname - The hostname to extract TLD from
 * @returns The TLD domain with leading dot (e.g., ".example.com")
 */
function getTldDomain(hostname: string): string {
  const parsed = psl.parse(hostname);
  if (parsed.error || !("domain" in parsed) || !parsed.domain) {
    return hostname; // fallback to current hostname
  }
  return `.${parsed.domain}`; // e.g., ".example.com"
}

/**
 * Gets the cookie domain for the current environment
 */
function getCookieDomain(): string | undefined {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!globalThis.window?.location) {
    return undefined;
  }
  return getTldDomain(globalThis.window.location.hostname);
}

export function createJourneyManager(config: Config): JourneyManager {
  const { storage, storageKey = "__vol-jid", overrideId, cookieName = "__volumental-jid", cookieExpires = 30 } = config;

  /**
   * Gets the ID from the cookie
   */
  function getFromCookie(): string | undefined {
    return Cookies.get(cookieName);
  }

  /**
   * Sets the ID in the cookie with TLD wildcard domain
   */
  function setInCookie(id: string): void {
    const domain = getCookieDomain();
    Cookies.set(cookieName, id, {
      expires: cookieExpires,
      path: "/",
      sameSite: "Lax",
      ...(domain ? { domain } : {}),
    });
  }

  /**
   * Gets the current journey ID, using override if provided, otherwise from cookie/storage
   * @returns The journey ID
   */
  function GetId(): JourneyId {
    // Use override ID if provided (doesn't persist to storage)
    if (overrideId) {
      return overrideId as JourneyId;
    }

    // Check cookie first
    const cookieId = getFromCookie();
    if (cookieId) {
      return cookieId as JourneyId;
    }

    // Check legacy storage (localStorage) for migration
    if (storage) {
      const legacyId = storage.get(storageKey);
      if (legacyId) {
        // Migrate to cookie
        setInCookie(legacyId);
        return legacyId as JourneyId;
      }
    }

    // Generate new ID if none exists
    const newId = generateId();
    setInCookie(newId);
    return newId;
  }

  /**
   * Checks if a journey ID exists (either as override, in cookie, or in legacy storage)
   * @returns True if ID exists, false otherwise
   */
  function Exists(): boolean {
    if (overrideId !== undefined) {
      return true;
    }

    // Check cookie first
    const cookieId = getFromCookie();
    if (cookieId !== undefined) {
      return true;
    }

    // Check legacy storage
    if (storage) {
      return storage.get(storageKey) !== undefined;
    }

    return false;
  }

  return {
    GetId,
    Exists,
  };
}

/**
 * Utility to extract journey ID from URL parameters
 */
export function getJourneyIdFromUrl(paramName = "jid"): JourneyId | undefined {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!globalThis.window?.location) {
    return undefined;
  }

  const urlParams = new URLSearchParams(globalThis.window.location.search);
  const value = urlParams.get(paramName);
  return value ? (value as JourneyId) : undefined;
}
